import SEO from "@/components/SEO";
import { PageHero, Section, SectionHead, FAQ } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CTALink } from "@/components/ui-kit";
import LeadForm from "@/components/LeadForm";
import { Link } from "react-router-dom";
import { Landmark, ShieldCheck, HeartPulse, Car, Building2 } from "lucide-react";

const fixedIncome = [
  { icon: Landmark, name: "Corporate & secured bonds", desc: "Higher-yield and secured bonds for income and stability, with issuer, security and liquidity explained upfront." },
  { icon: Building2, name: "Fixed-income for treasury", desc: "For businesses, structured fixed-income options to deploy idle capital with clear risk boundaries." },
];

const insurance = [
  { icon: HeartPulse, name: "Health & personal accident", desc: "Protect savings from medical shocks with the right sum insured and features for your family." },
  { icon: Car, name: "Motor insurance", desc: "Comprehensive motor cover arranged as part of your overall protection plan." },
  { icon: ShieldCheck, name: "Corporate & keyman cover", desc: "Group health, liability and keyman policies so a single event doesn't threaten the business." },
];

const faq = [
  { q: "Are bond returns guaranteed?", a: "No. Coupons depend on the issuer honouring obligations; prices move with rates and credit. Returns are potential and subject to market and credit risk." },
  { q: "Why buy insurance through an advisor?", a: "We match cover to your actual exposures and coordinate it with your investments — not sell a standalone policy. Insurance is the subject matter of solicitation." },
];

export default function FixedIncomeInsurance() {
  return (
    <div>
      <SEO path="/products/fixed-income-insurance" title="Fixed Income & Insurance" description="Corporate and secured bonds plus health, motor and corporate insurance — income and protection planned together by Rao Investment Solutions." faq={faq} />
      <PageHero
        label="Products"
        title="Fixed Income & Insurance"
        intro="Income and protection are two sides of a resilient plan. We arrange both — for balance sheets and households alike."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Products" }, { name: "Fixed Income & Insurance" }]}
      />

      <Section className="!pt-0">
        <SectionHead label="Fixed Income" title="Income with clarity on risk" />
        <div className="grid md:grid-cols-2 gap-6">
          {fixedIncome.map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="h-full bg-white p-8 md:p-10 border border-navy/10 hover:shadow-lg transition-shadow">
                <f.icon className="w-8 h-8 text-orange" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl text-navy">{f.name}</h3>
                <p className="mt-3 font-sans text-base text-slate leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><CTALink to="/calculators" variant="secondary" data-testid="fii-bond-calc-cta">Compare a bond vs FD</CTALink></div>
      </Section>

      <Section cream>
        <SectionHead label="Insurance" title="Protection, sized to real exposures" intro="Insurance is the subject matter of solicitation." />
        <div className="grid md:grid-cols-3 gap-6">
          {insurance.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="h-full bg-white p-8 border border-navy/10 hover:shadow-lg transition-shadow">
                <f.icon className="w-7 h-7 text-orange" strokeWidth={1.25} />
                <h3 className="mt-5 font-serif text-xl text-navy">{f.name}</h3>
                <p className="mt-3 font-sans text-sm text-slate leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl"><SectionHead label="FAQ" title="Good to know" /><FAQ items={faq} /></div>
      </Section>

      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <SectionHead label="Next step" title="Balance income with protection." dark intro="We'll plan both together so your portfolio and your safety net work as one." />
          <div className="bg-white/5 border border-white/10 p-8 md:p-10"><LeadForm dark interest="Fixed Income & Insurance" title="Request a plan" /></div>
        </div>
      </Section>
    </div>
  );
}
